import type { MLCEngineInterface } from '@mlc-ai/web-llm';
import { createBrowserInterpretationMessages } from './client';
import type { InterpretationInput, StreamInterpretationOptions } from './client';

/**
 * 默认使用 Qwen3 0.6B，在普通电脑上平衡中文表达与下载体积；
 * 1.7B 由用户主动选择，0.5B 只作为兼容回退。
 */
export const BROWSER_MODEL_ID = 'Qwen3-0.6B-q4f16_1-MLC';
export const BROWSER_MODEL_QUALITY_ID = 'Qwen3-1.7B-q4f16_1-MLC';
export const BROWSER_MODEL_FALLBACK_ID = 'Qwen2.5-0.5B-Instruct-q4f16_1-MLC';
export const BROWSER_MODEL_IDS = [BROWSER_MODEL_ID, BROWSER_MODEL_QUALITY_ID, BROWSER_MODEL_FALLBACK_ID] as const;
export type BrowserModelId = typeof BROWSER_MODEL_IDS[number];
export const LEGACY_BROWSER_MODEL_IDS = [
  'Llama-3.2-1B-Instruct-q4f16_1-MLC',
  'Llama-3.2-1B-Instruct-q4f32_1-MLC',
  'SmolLM2-360M-Instruct-q4f32_1-MLC'
] as const;

const enginePromises = new Map<string, Promise<MLCEngineInterface>>();
let legacyCacheCleanupPromise: Promise<void> | undefined;

/**
 * 删除不再使用的 Llama 与 SmolLM2 缓存。IndexedDB 和 Cache Storage
 * 都清理，但不会碰仍在使用的三档 Qwen 缓存；失败时不阻断正常解读。
 */
export async function clearLegacyBrowserModelCache(): Promise<void> {
  if (!legacyCacheCleanupPromise) {
    legacyCacheCleanupPromise = (async () => {
      try {
        const { deleteModelAllInfoInCache, prebuiltAppConfig } = await import('@mlc-ai/web-llm');
        for (const modelId of LEGACY_BROWSER_MODEL_IDS) {
          for (const useIndexedDBCache of [true, false]) {
            try {
              await deleteModelAllInfoInCache(modelId, { ...prebuiltAppConfig, useIndexedDBCache });
            } catch {
              // 旧缓存可能只存在其中一种存储中，单个存储清理失败不应阻断当前模型。
            }
          }
        }
      } catch {
        // 旧版本 WebLLM 没有删除 API 时，保留当前模型的正常启动路径。
      }
    })();
  }
  await legacyCacheCleanupPromise;
}

export interface BrowserModelCapabilities {
  webGpu: boolean;
  secureContext: boolean;
}

export interface BrowserInterpretationOptions extends StreamInterpretationOptions {
  modelId?: BrowserModelId;
}

function generationOptions(modelId: BrowserModelId) {
  if (modelId === BROWSER_MODEL_QUALITY_ID) {
    return {
      temperature: 0.42,
      top_p: 0.9,
      repetition_penalty: 1.12,
      frequency_penalty: 0.18,
      presence_penalty: 0.1,
      max_tokens: 240
    };
  }
  if (modelId === BROWSER_MODEL_ID) {
    return {
      temperature: 0.3,
      top_p: 0.82,
      repetition_penalty: 1.16,
      frequency_penalty: 0.24,
      presence_penalty: 0.05,
      max_tokens: 200
    };
  }
  return {
    temperature: 0.25,
    top_p: 0.78,
    repetition_penalty: 1.18,
    frequency_penalty: 0.28,
    presence_penalty: 0.05,
    max_tokens: 180
  };
}

export function getBrowserModelCapabilities(): BrowserModelCapabilities {
  return {
    webGpu: typeof navigator !== 'undefined' && 'gpu' in navigator,
    secureContext: typeof window === 'undefined' || window.isSecureContext
  };
}

/**
 * 把浏览器/WebGPU 的底层错误转换成可以直接执行的中文提示。
 * WebLLM 在不同浏览器和显卡驱动上可能只返回同一类通用着色器错误，
 * 这里不把“支持 WebGPU”误报成“必定支持当前模型”。
 */
export function formatBrowserModelError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/Invalid ShaderModule|Invalid Shader Module|GPUPipelineError|index_kernel/i.test(message)) {
    return '当前浏览器的 WebGPU 无法编译本地模型着色器（index_kernel）。这不是 API Key 或下载失败：请更新 Chrome 与显卡驱动后完全重启浏览器；如果仍然失败，请改用本机 Ollama 或兼容接口。';
  }
  if (/device was lost|device lost|GPUDeviceLost|VK_ERROR_DEVICE_LOST|DXGI_ERROR_DEVICE_HUNG/i.test(message)) {
    return '当前 GPU 在加载本地模型时丢失了 WebGPU 设备，通常与显卡驱动或显存限制有关。请更新驱动并重启 Chrome；若仍失败，请改用本机 Ollama 或兼容接口。';
  }
  if (/out of memory|memory|显存/i.test(message)) {
    return '本地模型占用的显存或内存不足。请关闭占用 GPU 的程序后重试，或改用本机 Ollama/兼容接口。';
  }
  if (/Cache\.add\(|network error|failed to fetch|网络错误/i.test(message)) {
    return '本地模型文件下载失败，通常是网络、代理或缓存被中断。请保持网络稳定后重试；如果反复失败，可改用本机 Ollama 或兼容接口。';
  }
  return message || '浏览器本地模型启动失败，请检查 Chrome、WebGPU 和显卡驱动。';
}

function isCompatibilityError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Invalid ShaderModule|Invalid Shader Module|GPUPipelineError|index_kernel|device was lost|device lost|GPUDeviceLost|VK_ERROR_DEVICE_LOST|DXGI_ERROR_DEVICE_HUNG|out of memory|显存|Cache\.add\(|network error|failed to fetch|网络错误/i.test(message);
}

function localizeProgress(text: string): string {
  const percent = text.match(/(\d+)%/)?.[1];
  const fetched = text.match(/(\d+(?:\.\d+)?\s*(?:MB|GB))\s+fetched/i)?.[1];
  if (/fetching param cache/i.test(text)) {
    return `正在下载模型参数缓存${fetched ? `（已下载 ${fetched}` : ''}${percent ? `${fetched ? '，' : '（'}${percent}%` : ''}${fetched || percent ? '）' : ''}；首次使用需要一些时间，之后会更快。`;
  }
  if (/start to fetch params/i.test(text)) return '开始下载模型参数，首次使用需要一些时间。';
  if (/loading model|load model/i.test(text)) return '正在加载浏览器本地模型，请稍候。';
  if (/finish|complete|done/i.test(text)) return '本地模型加载完成，正在生成解读。';
  return '浏览器本地模型处理中，请稍候。';
}

function ensureWebGpu(): void {
  if (!getBrowserModelCapabilities().webGpu) {
    throw new Error('当前浏览器不支持 WebGPU。请使用最新版 Chrome，并确认系统图形加速已开启；也可以改用 Ollama 或远程兼容接口。');
  }
}

async function getEngine(modelId: string, onStatus?: (status: string) => void): Promise<MLCEngineInterface> {
  const existing = enginePromises.get(modelId);
  if (existing) return existing;

  const pending = import('@mlc-ai/web-llm')
    .then(({ CreateMLCEngine, prebuiltAppConfig }) => CreateMLCEngine(modelId, {
      // IndexedDB 不会调用 Cache.add()，能避开部分浏览器代理/缓存实现的网络错误。
      appConfig: { ...prebuiltAppConfig, useIndexedDBCache: true },
      initProgressCallback: (report) => onStatus?.(localizeProgress(report.text))
    }))
    .catch((error) => {
      enginePromises.delete(modelId);
      throw error;
    });
  enginePromises.set(modelId, pending);
  return pending;
}

export async function streamBrowserInterpretation(
  input: InterpretationInput,
  options: BrowserInterpretationOptions = {}
): Promise<string> {
  if (options.signal?.aborted) throw new DOMException('已中止', 'AbortError');
  ensureWebGpu();
  options.onStatus?.('正在准备浏览器本地模型；第一次使用会下载并缓存模型文件。');
  let lastError: unknown;
  const preferredModelId = options.modelId ?? BROWSER_MODEL_ID;
  const modelIds: BrowserModelId[] = preferredModelId === BROWSER_MODEL_QUALITY_ID
    ? [BROWSER_MODEL_QUALITY_ID, BROWSER_MODEL_ID, BROWSER_MODEL_FALLBACK_ID]
    : preferredModelId === BROWSER_MODEL_ID
      ? [BROWSER_MODEL_ID, BROWSER_MODEL_FALLBACK_ID]
      : [BROWSER_MODEL_FALLBACK_ID];
  for (const [index, modelId] of modelIds.entries()) {
    if (options.signal?.aborted) throw new DOMException('已中止', 'AbortError');
    if (index === 0) {
      options.onStatus?.('正在清理不再使用的旧模型缓存；不会删除当前 Qwen 缓存。');
      await clearLegacyBrowserModelCache();
      if (options.signal?.aborted) throw new DOMException('已中止', 'AbortError');
    }
    const pendingEngine = getEngine(modelId, options.onStatus);
    let engine: MLCEngineInterface;
    let abortLoading: (() => void) | undefined;
    const aborted = options.signal ? new Promise<never>((_resolve, reject) => {
      abortLoading = () => reject(new DOMException('已中止', 'AbortError'));
      options.signal?.addEventListener('abort', abortLoading, { once: true });
    }) : undefined;
    try {
      engine = await (aborted ? Promise.race([pendingEngine, aborted]) : pendingEngine);
    } catch (error) {
      lastError = error;
      if (!isCompatibilityError(error) || index === modelIds.length - 1) throw error;
      options.onStatus?.('当前模型与本机 GPU 不兼容，正在切换较小的兼容模型；无需重新填写内容。');
      continue;
    } finally {
      if (abortLoading) options.signal?.removeEventListener('abort', abortLoading);
    }
    if (options.signal?.aborted) throw new DOMException('已中止', 'AbortError');

    let result = '';
    const interrupt = () => {
      void engine.interruptGenerate();
    };
    options.signal?.addEventListener('abort', interrupt, { once: true });
    try {
      const chunks = await engine.chat.completions.create({
        messages: createBrowserInterpretationMessages(input),
        stream: true,
        ...generationOptions(modelId)
      });
      for await (const chunk of chunks) {
        if (options.signal?.aborted) throw new DOMException('已中止', 'AbortError');
        const token = chunk.choices[0]?.delta?.content ?? '';
        if (token) {
          result += token;
          options.onToken?.(token);
        }
      }
    } catch (error) {
      lastError = error;
      if (!isCompatibilityError(error) || result.trim() || index === modelIds.length - 1) throw error;
      enginePromises.delete(modelId);
      options.onStatus?.('当前模型生成内核不兼容，正在切换备用模型；无需重新填写内容。');
      continue;
    } finally {
      options.signal?.removeEventListener('abort', interrupt);
    }
    if (result.trim()) return result;
    lastError = new Error('浏览器本地模型没有返回可显示的文本。');
    if (index < modelIds.length - 1) {
      options.onStatus?.('当前模型没有返回内容，正在切换备用模型。');
      enginePromises.delete(modelId);
      continue;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('浏览器本地模型启动失败。');
}

/** 仅供测试隔离使用，生产代码不会调用。 */
export function resetBrowserModelState(): void {
  enginePromises.clear();
  legacyCacheCleanupPromise = undefined;
}
